import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/brand/PageHeader';
import { Panel } from '@/components/brand/Panel';
import { SevPill } from '@/components/brand/SevPill';
import { useApiQuery } from '@/lib/hooks';
import { ApiError } from '@/lib/api';
import { EmptyState, ErrorState, LoadingState, UnauthorizedState } from '@/components/brand/States';
import { useAuth } from '@/lib/auth';
import { thirtyDayWindow } from '@/lib/time';

interface AlertRule {
  id: string;
  name: string;
  source: string | null;
  ruleType: string;
  metric: string;
  operator: 'gt' | 'lt' | 'eq';
  threshold: number;
  windowMinutes: number;
  severity: 'info' | 'warning' | 'critical';
  enabled: number;
  createdBy: string;
}

function RuleRow({ rule: r, isAdmin, onChanged }: { rule: AlertRule; isAdmin: boolean; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<{ threshold: number; windowMinutes: number; severity: AlertRule['severity'] }>({
    threshold: r.threshold,
    windowMinutes: r.windowMinutes,
    severity: r.severity,
  });

  const toggleEnabled = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/alerts/rules/${r.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: r.enabled !== 1 }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
    },
    onSuccess: onChanged,
  });

  const saveEdits = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/alerts/rules/${r.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          threshold: draft.threshold,
          window_minutes: draft.windowMinutes,
          severity: draft.severity,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
    },
    onSuccess: () => {
      setEditing(false);
      onChanged();
    },
  });

  const del = useMutation({
    mutationFn: async () => {
      if (!confirm(`Delete rule "${r.name}"?`)) return;
      const res = await fetch(`/api/alerts/rules/${r.id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    },
    onSuccess: onChanged,
  });

  if (editing) {
    return (
      <tr className="bg-bg-section/30">
        <td className="py-2">{r.name}</td>
        <td className="mono">{r.ruleType}</td>
        <td className="mono">{r.source}</td>
        <td>
          <input type="number" value={draft.threshold} onChange={(e) => setDraft({ ...draft, threshold: Number(e.target.value) })} className="w-20 border border-rule px-2 py-1 bg-bg-page num text-small" />
          {' '}/{' '}
          <input type="number" value={draft.windowMinutes} onChange={(e) => setDraft({ ...draft, windowMinutes: Number(e.target.value) })} className="w-16 border border-rule px-2 py-1 bg-bg-page num text-small" />m
        </td>
        <td>
          <select value={draft.severity} onChange={(e) => setDraft({ ...draft, severity: e.target.value as typeof draft.severity })} className="border border-rule px-2 py-1 bg-bg-page text-small">
            <option value="info">info</option>
            <option value="warning">warning</option>
            <option value="critical">critical</option>
          </select>
        </td>
        {isAdmin && (
          <td className="text-right space-x-2">
            <button onClick={() => saveEdits.mutate()} disabled={saveEdits.isPending} className="text-eyebrow border border-rule px-2 py-1 disabled:opacity-40">
              {saveEdits.isPending ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => setEditing(false)} className="text-eyebrow text-fg-muted">Cancel</button>
          </td>
        )}
      </tr>
    );
  }

  return (
    <tr className={r.enabled === 1 ? '' : 'opacity-50'}>
      <td className="py-2">{r.name}</td>
      <td className="mono">{r.ruleType}</td>
      <td className="mono">{r.source}</td>
      <td className="num">{r.operator} {r.threshold} / {r.windowMinutes}m</td>
      <td>
        <SevPill level={r.severity === 'critical' ? 'crit' : r.severity === 'warning' ? 'warn' : 'info'} label={r.severity} />
      </td>
      {isAdmin && (
        <td className="text-right space-x-3 text-caption">
          <button onClick={() => toggleEnabled.mutate()} className="text-fg-muted hover:text-fg-default">
            {r.enabled === 1 ? 'Disable' : 'Enable'}
          </button>
          <button onClick={() => setEditing(true)} className="text-fg-muted hover:text-fg-default">Edit</button>
          <button onClick={() => del.mutate()} className="text-fg-muted hover:text-sev-crit">Delete</button>
        </td>
      )}
    </tr>
  );
}

interface AlertHistoryRow {
  id: string;
  ruleId: string;
  state: 'triggered' | 'resolved';
  triggeredAt: number;
  resolvedAt: number | null;
  acknowledgedAt: number | null;
  acknowledgedBy: string | null;
  message: string;
}

function RuleBuilder({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState('');
  const [source, setSource] = useState('cloudflare_worker');
  const [ruleType, setRuleType] = useState<'failure' | 'silence' | 'budget_pct' | 'event_match'>(
    'failure',
  );
  const [metric, setMetric] = useState('failed_count');
  const [operator, setOperator] = useState<'gt' | 'lt' | 'eq'>('gt');
  const [threshold, setThreshold] = useState(2);
  const [windowMinutes, setWindowMinutes] = useState(5);
  const [severity, setSeverity] = useState<'info' | 'warning' | 'critical'>('warning');
  const [useCustomChannels, setUseCustomChannels] = useState(false);
  const [channelTeams, setChannelTeams] = useState(true);
  const [channelEmail, setChannelEmail] = useState(true);

  const m = useMutation({
    mutationFn: async () => {
      const channels = useCustomChannels
        ? [
            ...(channelTeams ? ['teams' as const] : []),
            ...(channelEmail ? ['email' as const] : []),
          ]
        : undefined;
      const res = await fetch('/api/alerts/rules', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          source,
          rule_type: ruleType,
          metric,
          operator,
          threshold,
          window_minutes: windowMinutes,
          severity,
          ...(channels ? { channels } : {}),
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
      return res.json();
    },
    onSuccess: () => {
      setName('');
      onCreated();
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (name) m.mutate();
      }}
      className="grid grid-cols-1 md:grid-cols-2 gap-3 text-small"
    >
      <label className="flex flex-col gap-1">
        <span className="eyebrow">Name</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className="border border-rule px-3 py-2 bg-bg-page"
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="eyebrow">Source</span>
        <select value={source} onChange={(e) => setSource(e.target.value)} className="border border-rule px-3 py-2 bg-bg-page">
          <option value="cloudflare_worker">cloudflare_worker</option>
          <option value="hubspot">hubspot</option>
          <option value="sap_b1">sap_b1</option>
          <option value="m365">m365</option>
          <option value="m365_audit">m365_audit</option>
          <option value="egnyte_audit">egnyte_audit</option>
          <option value="anthropic">anthropic</option>
        </select>
      </label>
      <label className="flex flex-col gap-1">
        <span className="eyebrow">Rule type</span>
        <select
          value={ruleType}
          onChange={(e) => setRuleType(e.target.value as typeof ruleType)}
          className="border border-rule px-3 py-2 bg-bg-page"
        >
          <option value="failure">failure</option>
          <option value="silence">silence</option>
          <option value="budget_pct">budget_pct</option>
          <option value="event_match">event_match</option>
        </select>
        {ruleType === 'event_match' && (
          <span className="text-micro text-fg-muted">
            metric = severity floor (critical / warning); fires when count over the window beats
            the threshold. Leave source blank to watch both audit feeds.
          </span>
        )}
      </label>
      <label className="flex flex-col gap-1">
        <span className="eyebrow">Severity</span>
        <select
          value={severity}
          onChange={(e) => setSeverity(e.target.value as typeof severity)}
          className="border border-rule px-3 py-2 bg-bg-page"
        >
          <option value="info">info</option>
          <option value="warning">warning</option>
          <option value="critical">critical</option>
        </select>
      </label>
      <label className="flex flex-col gap-1">
        <span className="eyebrow">Metric</span>
        <input value={metric} onChange={(e) => setMetric(e.target.value)} className="border border-rule px-3 py-2 bg-bg-page mono" />
      </label>
      <label className="flex flex-col gap-1">
        <span className="eyebrow">Operator</span>
        <select value={operator} onChange={(e) => setOperator(e.target.value as 'gt' | 'lt' | 'eq')} className="border border-rule px-3 py-2 bg-bg-page">
          <option value="gt">gt</option>
          <option value="lt">lt</option>
          <option value="eq">eq</option>
        </select>
      </label>
      <label className="flex flex-col gap-1">
        <span className="eyebrow">Threshold</span>
        <input type="number" value={threshold} onChange={(e) => setThreshold(Number(e.target.value))} className="border border-rule px-3 py-2 bg-bg-page num" />
      </label>
      <label className="flex flex-col gap-1">
        <span className="eyebrow">Window (min)</span>
        <input type="number" value={windowMinutes} onChange={(e) => setWindowMinutes(Number(e.target.value))} className="border border-rule px-3 py-2 bg-bg-page num" />
      </label>
      <div className="md:col-span-2 border-t border-rule pt-3 mt-1">
        <label className="flex items-center gap-2 text-small mb-2">
          <input
            type="checkbox"
            checked={useCustomChannels}
            onChange={(e) => setUseCustomChannels(e.target.checked)}
          />
          <span className="eyebrow">Override notification routing for this rule</span>
        </label>
        {useCustomChannels ? (
          <div className="flex gap-4 text-small ml-6">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={channelTeams} onChange={(e) => setChannelTeams(e.target.checked)} />
              Teams
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={channelEmail} onChange={(e) => setChannelEmail(e.target.checked)} />
              Email
            </label>
          </div>
        ) : (
          <p className="text-fg-muted text-caption ml-6">
            Will use the global severity routing (warning/critical → Teams + email; info → dashboard only).
          </p>
        )}
      </div>
      <div className="md:col-span-2 flex items-center justify-between mt-2">
        <p className="text-fg-muted text-caption">All writes appended to admin_audit_log.</p>
        <button
          type="submit"
          disabled={m.isPending || !name}
          className="bg-fg-default text-mb-spring-wood px-4 py-2 text-eyebrow disabled:opacity-40"
        >
          {m.isPending ? 'Saving…' : 'Create rule'}
        </button>
      </div>
      {m.error && <p className="md:col-span-2 text-sev-crit text-caption mono">{String(m.error.message)}</p>}
    </form>
  );
}

export function AlertsPage() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const { to: now } = thirtyDayWindow();
  const rulesQ = useApiQuery<{ items: AlertRule[] }>({
    queryKey: ['alerts/rules'],
    queryFn: async () => {
      const res = await fetch('/api/alerts/rules', { credentials: 'include' });
      if (!res.ok) {
        const err: any = new Error(`HTTP ${res.status}`);
        err.status = res.status;
        if (res.status === 401) {
          const e = new (class extends Error {
            isUnauthorized() { return true; }
          })('unauthorized');
          throw e;
        }
        throw err;
      }
      return res.json();
    },
  });

  const historyQ = useApiQuery<{ items: AlertHistoryRow[] }>({
    queryKey: ['alerts/history', now],
    queryFn: async () => {
      const res = await fetch(`/api/alerts/history?from=${now - 30 * 24 * 60 * 60 * 1000}&to=${now}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
  });

  if (rulesQ.error instanceof ApiError && rulesQ.error.isUnauthorized()) return <UnauthorizedState />;

  return (
    <div className="space-y-5">
      <PageHeader eyebrow="Notifications" title="Alerts" />
      {me?.role === 'admin' && (
        <Panel title="Add rule" subtitle="admin">
          <RuleBuilder onCreated={() => qc.invalidateQueries({ queryKey: ['alerts/rules'] })} />
        </Panel>
      )}
      <Panel title="Active rules">
        {rulesQ.isLoading && <LoadingState />}
        {rulesQ.error && <ErrorState title="Couldn't load rules" detail={String((rulesQ.error as Error).message)} />}
        {rulesQ.data && rulesQ.data.items.length === 0 && <EmptyState title="No rules configured yet" hint="Add one above to start the alert engine evaluating events." />}
        {rulesQ.data && rulesQ.data.items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-small">
              <thead className="text-fg-muted text-eyebrow">
                <tr>
                  <th className="text-left py-2">Name</th>
                  <th className="text-left">Type</th>
                  <th className="text-left">Source</th>
                  <th className="text-left">Threshold</th>
                  <th className="text-left">Severity</th>
                  {me?.role === 'admin' && <th className="text-right">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-rule">
                {rulesQ.data.items.map((r) => (
                  <RuleRow key={r.id} rule={r} isAdmin={me?.role === 'admin'} onChanged={() => qc.invalidateQueries({ queryKey: ['alerts/rules'] })} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      <Panel title="History" subtitle="Last 30 days">
        {historyQ.isLoading && <LoadingState />}
        {historyQ.error && <ErrorState title="Couldn't load history" detail={String((historyQ.error as Error).message)} />}
        {historyQ.data && historyQ.data.items.length === 0 && <EmptyState title="No alerts fired" />}
        {historyQ.data && historyQ.data.items.length > 0 && (
          <ul className="divide-y divide-rule">
            {historyQ.data.items.map((h) => {
              const resolvedDelta =
                h.state === 'resolved' && h.resolvedAt
                  ? Math.max(0, Math.round((h.resolvedAt - h.triggeredAt) / 60_000))
                  : null;
              return (
                <li
                  key={h.id}
                  className={
                    h.state === 'resolved'
                      ? 'py-3 text-small flex items-center justify-between opacity-60'
                      : 'py-3 text-small flex items-center justify-between'
                  }
                >
                  <div>
                    <a
                      href={`/alerts/history/${h.id}`}
                      className="text-fg-default border-b-0 hover:underline"
                    >
                      {h.message}
                    </a>
                    <p className="text-fg-muted text-caption mono mt-1">
                      <span title={new Date(h.triggeredAt).toISOString()}>
                        triggered {new Date(h.triggeredAt).toISOString()}
                      </span>
                      {resolvedDelta != null && (
                        <>
                          <span className="mx-2">·</span>
                          <span title={h.resolvedAt ? new Date(h.resolvedAt).toISOString() : ''}>
                            resolved after {resolvedDelta}m
                          </span>
                        </>
                      )}
                      {h.acknowledgedAt && (
                        <>
                          <span className="mx-2">·</span>
                          <span title={new Date(h.acknowledgedAt).toISOString()}>
                            ack by {h.acknowledgedBy}
                          </span>
                        </>
                      )}
                    </p>
                  </div>
                  {h.state === 'resolved' ? (
                    <SevPill level="ok" label="resolved" />
                  ) : h.acknowledgedAt ? (
                    <SevPill level="warn" label="acknowledged" />
                  ) : (
                    <SevPill level="crit" label="open" />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Panel>
    </div>
  );
}
